/**
 * FO-457 (spec 157) - groepering voor de klantportaal-weekplanning.
 *
 * De klant-planning rendert(e) elke dienst als losse kolom op een uurraster.
 * Bij een klant met veel gelijktijdige inzet (10-15 monteurs per dag) wordt elke
 * kolom een paar pixels breed en valt er niets meer af te lezen: geen naam, geen
 * tijd, geen dienst. De informatiebehoefte van de klant is niet "waar zit dit
 * blok op de tijdlijn" maar "hoeveel mensen staan er wanneer, en wie" - en
 * ploegdiensten hebben grotendeels IDENTIEKE tijden.
 *
 * Daarom bundelen we per dag de diensten met dezelfde soort, tijden en locatie
 * tot één kaart ("Dagdienst 07:00-16:00 · 6 monteurs"). Deze module is de ENE
 * bron voor die groepering: weekweergave, dagdetail en de PDF-/Excel-export
 * lezen er allemaal uit, zodat de export het scherm nooit kan tegenspreken (T6).
 *
 * Alles puur: geen DOM, geen I/O, geen `Date`-tijdzone-afhankelijkheid en geen
 * zichtbare tekst. Labels zijn i18n-verantwoordelijkheid van de klant-app (NL/EN);
 * hier komen alleen sleutels en getallen uit.
 */
import type { KlantPlanningStatus } from './klant-planning-status'
import { KLANT_PLANNING_STATUSSEN } from './klant-planning-status'
import { parseHHmmToMinutes } from './werkbon-hours'
import { isNachtdienst } from './planning-dienst'

/**
 * Diensttype waarop gegroepeerd en gekleurd wordt. Bewust een kleine, gesloten
 * set: de klant hoeft het interne ploegrooster niet te kennen, hij wil het
 * RITME zien (dag/avond/nacht) en herkennen wat standby is.
 */
export const KLANT_DIENST_SOORTEN = [
  'DAG',
  'AVOND',
  'NACHT',
  'CONSIGNATIE',
  'OPROEP',
] as const

export type KlantDienstSoort = (typeof KLANT_DIENST_SOORTEN)[number]

/** Vanaf deze begintijd heet een dienst "avond". */
const AVOND_START_MIN = 14 * 60
/** Vanaf deze begintijd (of ervoor, zie `VROEGE_NACHT_MIN`) heet een dienst "nacht". */
const NACHT_START_MIN = 22 * 60
/** Vóór deze begintijd valt een dienst ook onder "nacht" (bv. 04:00 - 12:00). */
const VROEGE_NACHT_MIN = 6 * 60

/** Minimale vorm van een dienst voor de soort-bepaling. */
export interface KlantDienstSoortInput {
  startTijd: string
  eindTijd: string
  isConsignatie: boolean
  isOproep: boolean
}

/**
 * Het diensttype van een dienst.
 *
 * Volgorde is bewust: een oproep binnen een consignatiedienst is voor de klant
 * iets anders dan de standby zelf (andere chip, andere urenlezing), dus die wint.
 * Daarna telt of de dienst over middernacht loopt; pas als laatste de begintijd.
 * Ongeldige tijden vallen terug op `DAG` - een dienst mag nooit stil uit de
 * groepering vallen omdat zijn tijden niet te parsen zijn.
 */
export function bepaalDienstSoort(dienst: KlantDienstSoortInput): KlantDienstSoort {
  if (dienst.isOproep) return 'OPROEP'
  if (dienst.isConsignatie) return 'CONSIGNATIE'
  if (isNachtdienst(dienst.startTijd, dienst.eindTijd)) return 'NACHT'
  const startMin = parseHHmmToMinutes(dienst.startTijd)
  if (startMin == null) return 'DAG'
  if (startMin >= NACHT_START_MIN || startMin < VROEGE_NACHT_MIN) return 'NACHT'
  if (startMin >= AVOND_START_MIN) return 'AVOND'
  return 'DAG'
}

/**
 * Minimale vorm van een klant-dagregel. Bewust een structurele interface en geen
 * import van het klant-app-type: `packages/utils` mag niet van een app afhangen.
 * De app-typen (`PlanningDayItem`) en het API-export-item voldoen er allebei aan.
 */
export interface KlantDienstRegel {
  startTijd: string
  eindTijd: string
  uren: number
  locatie: string | null
  isConsignatie: boolean
  isOproep: boolean
  status: KlantPlanningStatus
  /** Weergavenaam van de geplande monteur, of leeg bij een onbemande dienst. */
  monteur: string
  /** True = "wordt ingepland"; er hoort dan nooit een naam getoond te worden. */
  nogToeTeWijzen: boolean
  /** De vaste monteur van deze dienst, of null bij losse inzet. */
  vasteMonteur: string | null
  /** De geplande monteur vervangt de vaste monteur. */
  vervangenDoor: string | null
  /** De vaste monteur is afwezig en er is nog geen vervanger. */
  vervangingWordtIngepland: boolean
}

/**
 * Eén naam-regel op een groepskaart. `naam: null` betekent "wordt ingepland" -
 * de util geeft nooit tekst terug, de app vertaalt dat (NL/EN).
 */
export interface KlantDienstNaam {
  naam: string | null
  /** Naam van de vervanger, als deze dienst een vervanging is. */
  vervangenDoor: string | null
  /** De vaste monteur is afwezig, vervanger nog onbekend. */
  vervangingWordtIngepland: boolean
}

/** Eén dienstgroep-kaart: gelijke soort, gelijke tijden, gelijke locatie. */
export interface KlantDienstGroep<T> {
  /** Stabiele React-key; ook de groepeersleutel. */
  sleutel: string
  soort: KlantDienstSoort
  startTijd: string
  eindTijd: string
  /** Locatie-label, of null wanneer de dienst er geen heeft. */
  locatie: string | null
  /** De onderliggende dagregels, in invoervolgorde. */
  items: T[]
  /** De namen op de kaart, alfabetisch; onbemande diensten achteraan. */
  namen: KlantDienstNaam[]
  /** Aantal diensten in deze groep (= aantal ingezette personen). */
  aantalMonteurs: number
  /** Hoeveel daarvan nog geen uitvoerder hebben. */
  aantalWordtIngepland: number
  /** Hoeveel daarvan een vervanging van de vaste monteur zijn. */
  aantalVervangen: number
  /** De statussen die in deze groep voorkomen, in vaste volgorde. */
  statussen: KlantPlanningStatus[]
  /** Som van de uren in deze groep. */
  totaalUren: number
}

/** De weergavenaam van één dagregel, of null als hij nog wordt ingepland. */
function naamVan(regel: KlantDienstRegel): KlantDienstNaam {
  // Zelfde lezing als het dienstblok: de klant ziet de VASTE monteur van de
  // dienst; de vervanging staat als toelichting op dezelfde regel. Zonder vaste
  // monteur zegt "wordt ingepland" al alles, dus dan geen vervangings-tekst.
  if (regel.vasteMonteur) {
    return {
      naam: regel.vasteMonteur,
      vervangenDoor: regel.vervangenDoor,
      vervangingWordtIngepland: regel.vervangenDoor
        ? false
        : regel.vervangingWordtIngepland,
    }
  }
  const naam = regel.nogToeTeWijzen || !regel.monteur ? null : regel.monteur
  return { naam, vervangenDoor: null, vervangingWordtIngepland: false }
}

/**
 * Deterministische naam-volgorde: alfabetisch op codepoint (geen `localeCompare`,
 * die kan per runtime verschillen en zou een hydration-mismatch geven tussen de
 * server-render en de browser). Onbemande diensten sluiten de rij.
 */
function vergelijkNamen(a: KlantDienstNaam, b: KlantDienstNaam): number {
  if (a.naam == null && b.naam == null) return 0
  if (a.naam == null) return 1
  if (b.naam == null) return -1
  if (a.naam === b.naam) return 0
  return a.naam < b.naam ? -1 : 1
}

/** Startminuut voor de groep-sortering; ongeldige tijden achteraan. */
function startMinutenVan(startTijd: string): number | null {
  return parseHHmmToMinutes(startTijd)
}

/**
 * Groepeert de dagregels van ÉÉN dag tot dienstgroepen.
 *
 * Groepeersleutel = diensttype + starttijd + eindtijd + locatie (spec §6.1/A2).
 * De locatie zit er bewust in: bij een klant met één locatie (of zonder
 * locatie-labels) is dat een no-op, en bij een multi-locatie-klant voorkomt het
 * dat twee ploegen op verschillende locaties onder één label samensmelten.
 *
 * Diensten met afwijkende tijden binnen dezelfde soort vormen dus een aparte
 * groep (T3), en één monteur op een dag levert gewoon één kaart met één naam op
 * (T7). De groepen staan op begintijd, dan eindtijd, dan soort.
 */
export function groepeerDienstenPerDag<T extends KlantDienstRegel>(
  regels: readonly T[],
): Array<KlantDienstGroep<T>> {
  const perSleutel = new Map<string, KlantDienstGroep<T>>()
  // Invoegvolgorde vasthouden zodat de sortering hieronder stabiel is.
  const volgorde: string[] = []

  for (const regel of regels) {
    const soort = bepaalDienstSoort(regel)
    const sleutel = `${soort}|${regel.startTijd}|${regel.eindTijd}|${regel.locatie ?? ''}`
    let groep = perSleutel.get(sleutel)
    if (!groep) {
      groep = {
        sleutel,
        soort,
        startTijd: regel.startTijd,
        eindTijd: regel.eindTijd,
        locatie: regel.locatie,
        items: [],
        namen: [],
        aantalMonteurs: 0,
        aantalWordtIngepland: 0,
        aantalVervangen: 0,
        statussen: [],
        totaalUren: 0,
      }
      perSleutel.set(sleutel, groep)
      volgorde.push(sleutel)
    }
    const naam = naamVan(regel)
    groep.items.push(regel)
    groep.namen.push(naam)
    groep.aantalMonteurs += 1
    if (naam.naam == null) groep.aantalWordtIngepland += 1
    if (naam.vervangenDoor) groep.aantalVervangen += 1
    groep.totaalUren += regel.uren
  }

  const groepen = volgorde
    .map((sleutel) => perSleutel.get(sleutel))
    .filter((g): g is KlantDienstGroep<T> => g != null)

  for (const groep of groepen) {
    groep.namen.sort(vergelijkNamen)
    // Vaste volgorde i.p.v. voorkomen-volgorde: de chips staan dan op elke kaart
    // in dezelfde leesrichting.
    groep.statussen = KLANT_PLANNING_STATUSSEN.filter((s) =>
      groep.items.some((item) => item.status === s),
    )
    // Afronden op 1 decimaal: de kaart toont "9.5u"; zonder afronding kruipt er
    // een 0.30000000000000004 in de som van drie diensten.
    groep.totaalUren = Math.round(groep.totaalUren * 100) / 100
  }

  return groepen.sort((a, b) => {
    const ma = startMinutenVan(a.startTijd)
    const mb = startMinutenVan(b.startTijd)
    if (ma == null && mb == null) return 0
    if (ma == null) return 1
    if (mb == null) return -1
    if (ma !== mb) return ma - mb
    if (a.eindTijd !== b.eindTijd) return a.eindTijd < b.eindTijd ? -1 : 1
    if (a.soort !== b.soort) {
      return KLANT_DIENST_SOORTEN.indexOf(a.soort) - KLANT_DIENST_SOORTEN.indexOf(b.soort)
    }
    return a.sleutel < b.sleutel ? -1 : 1
  })
}

/** De kop van een dagkolom: "12 monteurs · 3 diensten". */
export interface KlantDagSamenvatting {
  /** Aantal verschillende personen; onbemande diensten tellen elk als één. */
  aantalMonteurs: number
  /** Aantal dienstgroep-kaarten op deze dag. */
  aantalDiensten: number
  /** Ingeplande uren, exclusief consignatie (standby is geen werktijd). */
  totaalUren: number
}

/**
 * Vat een dag samen voor de kolomkop. Telt PERSONEN, niet dagregels: een monteur
 * met een ochtend- én een avonddienst is één monteur op twee diensten. Onbemande
 * diensten hebben geen naam om op te ontdubbelen en tellen daarom elk als één -
 * anders zou "3 monteurs worden nog ingepland" als 1 op het scherm komen.
 */
export function vatDagSamen<T extends KlantDienstRegel>(
  groepen: readonly KlantDienstGroep<T>[],
): KlantDagSamenvatting {
  const namen = new Set<string>()
  let onbemand = 0
  let totaalUren = 0
  for (const groep of groepen) {
    for (const naam of groep.namen) {
      if (naam.naam == null) onbemand += 1
      else namen.add(naam.naam)
    }
    for (const item of groep.items) {
      // Zelfde regel als het weektotaal in de API: consignatie-uren zijn geen
      // ingeplande werkuren, anders telt een standby-week dubbel.
      if (!item.isConsignatie) totaalUren += item.uren
    }
  }
  return {
    aantalMonteurs: namen.size + onbemand,
    aantalDiensten: groepen.length,
    totaalUren: Math.round(totaalUren * 100) / 100,
  }
}

/**
 * De locatie-labels die in deze regels voorkomen, gesorteerd. Leeg of één
 * element = de klant heeft geen multi-locatie-beeld nodig en de UI laat de
 * locatie weg (spec §3A.2).
 */
export function locatiesIn(regels: readonly KlantDienstRegel[]): string[] {
  const set = new Set<string>()
  for (const regel of regels) {
    if (regel.locatie) set.add(regel.locatie)
  }
  return [...set].sort((a, b) => (a === b ? 0 : a < b ? -1 : 1))
}

/** De diensttypen die in deze regels voorkomen, in vaste volgorde (voor het filter). */
export function dienstSoortenIn(regels: readonly KlantDienstRegel[]): KlantDienstSoort[] {
  const set = new Set<KlantDienstSoort>()
  for (const regel of regels) set.add(bepaalDienstSoort(regel))
  return KLANT_DIENST_SOORTEN.filter((s) => set.has(s))
}
